import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../api';

const AGENT_KEYS: Record<string, string> = {
  'test-agent': 'agent.test',
  'kvass-agent': 'agent.kvass_tester',
  'changelog-agent': 'agent.changelog',
  'testdata-agent': 'agent.testdata',
  'testdekning-agent': 'agent.coverage',
};

export default function Tasks() {
  const { t } = useTranslation();
  const [agents, setAgents] = useState<string[]>([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [taskText, setTaskText] = useState('');
  const [tasks, setTasks] = useState<any[]>([]);
  const [responses, setResponses] = useState<Record<string, string>>({});

  useEffect(() => {
    api.get('/agents').then(res => {
      const data = Array.isArray(res.data) ? res.data : [];
      const names = data.map((a: any) => a.name);
      setAgents(names);
      if (names.length > 0) setSelectedAgent(names[0]);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedAgent) return;
    api.get(`/tasks/${selectedAgent}`).then(res => {
      setTasks(Array.isArray(res.data.tasks) ? res.data.tasks : []);
    }).catch(() => {});
  }, [selectedAgent]);

  const submit = async () => {
    if (!taskText.trim()) return;
    await api.post(`/tasks/${selectedAgent}`, { text: taskText });
    setTaskText('');
    setTimeout(() => {
      api.get(`/tasks/${selectedAgent}`).then(res => {
        setTasks(Array.isArray(res.data.tasks) ? res.data.tasks : []);
      });
    }, 1000);
  };

  const loadResponse = async (filename: string) => {
    const res = await api.get(`/tasks/${selectedAgent}/${filename}`);
    if (res.data.ready) setResponses(prev => ({ ...prev, [filename]: res.data.response }));
    else setResponses(prev => ({ ...prev, [filename]: t('tasks.waiting') }));
  };

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">{t('tasks.title')}</h1>
      <div className="bg-gray-800 rounded-xl p-5 mb-6">
        <div className="mb-3">
          <label className="text-gray-400 text-sm block mb-1">{t('tasks.select_agent')}</label>
          <select className="bg-gray-700 text-white rounded px-3 py-2 w-full"
            value={selectedAgent} onChange={e => setSelectedAgent(e.target.value)}>
            {agents.map(a => (
              <option key={a} value={a}>
                {t(AGENT_KEYS[a] || a)}
              </option>
            ))}
          </select>
        </div>
        <div className="mb-3">
          <textarea
            className="w-full bg-gray-700 text-white rounded px-3 py-2 h-28 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder={t('tasks.placeholder')}
            value={taskText}
            onChange={e => setTaskText(e.target.value)}
          />
        </div>
        <button onClick={submit} className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded transition-colors">
          {t('tasks.submit')}
        </button>
      </div>
      <div className="space-y-3">
        {tasks.length === 0 && <div className="text-gray-500">{t('tasks.no_tasks')}</div>}
        {tasks.map(task => (
          <div key={task.input} className="bg-gray-800 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <span className="font-mono text-sm text-gray-300">{task.input}</span>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-1 rounded-full ${task.has_response ? 'bg-green-900 text-green-300' : 'bg-yellow-900 text-yellow-300'}`}>
                  {task.has_response ? '✓ Done' : '⏳ Pending'}
                </span>
                {task.has_response && (
                  <button onClick={() => loadResponse(task.input)} className="text-xs bg-blue-700 hover:bg-blue-600 px-2 py-1 rounded transition-colors">
                    {t('tasks.response')}
                  </button>
                )}
              </div>
            </div>
            {responses[task.input] && (
              <div className="mt-3 bg-gray-900 rounded p-3 text-sm text-gray-300 whitespace-pre-wrap max-h-64 overflow-auto">
                {responses[task.input]}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
